import * as React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { offerAvailability } from '@ooh/contracts';
import type {
  EndpointDef,
  callEndpoint,
  OfferAvailabilityResponse,
  PeriodGranularity,
} from '@ooh/contracts';
import { offerAvailabilityKeys, offerKeys } from '../../api/queries';

export { offerAvailabilityKeys, offerKeys };

export interface WithCallWithAuth {
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

export function useEffectiveAuth(auth?: WithCallWithAuth): WithCallWithAuth {
  const routeContext = useRouteContext({ from: '__root__' });
  return auth ?? (routeContext as { auth: WithCallWithAuth }).auth;
}

const AVAILABILITY_PAGE_SIZE = 50;

export interface UseOfferAvailabilityResult {
  units: OfferAvailabilityResponse['units'];
  defaultDateFrom: string | null;
  defaultDateTo: string | null;
  defaultPeriodGranularity: PeriodGranularity | null;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Cursor-based infinite query over `GET /offers/:offerId/availability`.
 *
 * Pages are flattened into `units`; the offer-level `defaultDateFrom`/
 * `defaultDateTo`/`defaultPeriodGranularity` fields are identical across
 * every page (they describe the offer, not the page), so they're sourced
 * from `pages[0]` only. No filtering — mirrors `useOfferUnits`'s/
 * `useOfferPrices`'s infinite-query wiring.
 */
export function useOfferAvailability(
  offerId: number,
  auth?: WithCallWithAuth,
): UseOfferAvailabilityResult {
  const effectiveAuth = useEffectiveAuth(auth);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, refetch } =
    useInfiniteQuery<
      OfferAvailabilityResponse,
      Error,
      { pages: OfferAvailabilityResponse[] },
      readonly [string, number],
      string | undefined
    >({
      queryKey: offerAvailabilityKeys.availability(offerId),
      queryFn: ({ pageParam }) =>
        effectiveAuth.callWithAuth(offerAvailability.getAvailability, {
          offerId,
          cursor: pageParam,
          pageSize: AVAILABILITY_PAGE_SIZE,
        }),
      placeholderData: (prev) => prev,
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => lastPage.meta?.nextCursor ?? undefined,
      staleTime: 30_000,
    });

  const units = React.useMemo(() => (data?.pages ?? []).flatMap((page) => page.units), [data]);
  const firstPage = data?.pages[0];

  return {
    units,
    defaultDateFrom: firstPage?.defaultDateFrom ?? null,
    defaultDateTo: firstPage?.defaultDateTo ?? null,
    defaultPeriodGranularity: firstPage?.defaultPeriodGranularity ?? null,
    hasNextPage,
    fetchNextPage: () => void fetchNextPage(),
    isFetchingNextPage,
    isLoading,
    isError,
    refetch: () => void refetch(),
  };
}
